import { FC, ReactNode, useState } from "react";

import { useFragment, useLazyLoadQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { MsgExec, priceFormatter } from "rujira.js";
import {
  AssetLabel,
  Decimal,
  DenomInput,
  Fiat,
  formatApr,
  i18n,
  IconDenom,
  Icons,
  nFormatter,
  Toggle,
} from "rujira.ui";
import { External } from "rujira.ui/src/components/icons/Icons";
import {
  BalanceProvider,
  usePreloadedBalance,
} from "../../common/components/Balance";
import { MsgProvider, TxButton } from "../../common/components/TxButton";
import { Subscription } from "../../services/useNodeSubscription";
import { StakingPoolPageAccountFragment$key } from "./__generated__/StakingPoolPageAccountFragment.graphql";
import { StakingPoolPageDepositFragment$key } from "./__generated__/StakingPoolPageDepositFragment.graphql";
import { StakingPoolPageFragment$key } from "./__generated__/StakingPoolPageFragment.graphql";
import { StakingPoolPageQuery } from "./__generated__/StakingPoolPageQuery.graphql";
import { AddressLink, StrategyContainer } from "./Common";
import { StakingPoolBalance } from "./StakingPoolBalance";

const fragment = graphql`
  fragment StakingPoolPageFragment on StakingPool {
    id
    address
    bondAsset {
      asset
      chain
      type
      metadata {
        symbol
        decimals
      }
      variants {
        native {
          denom
        }
      }
    }
    revenueAsset {
      asset
      metadata {
        symbol
      }
    }
    stakingSummary: summary {
      apr {
        value
        status
      }
    }
    status {
      id
      accountBond
      accountRevenue
      liquidBondShares
      liquidBondSize
      pendingRevenue
      valueUsd
    }
    ...StakingPoolPageDepositFragment
  }
`;

const subscription = graphql`
  subscription StakingPoolPageSubscription($id: ID!) {
    node(id: $id) {
      ... on StakingStatus {
        accountBond
        accountRevenue
        liquidBondShares
        liquidBondSize
        pendingRevenue
        valueUsd
      }
      ... on StakingSummary {
        apr {
          value
          status
        }
      }
    }
  }
`;

const accountFragment = graphql`
  fragment StakingPoolPageAccountFragment on Account {
    strategies {
      ... on StakingAccount {
        pool {
          id
        }
        ...StakingPoolBalanceFragment
      }
    }
  }
`;

export const StakingPool: FC<{
  k: StakingPoolPageFragment$key;
  a?: StakingPoolPageAccountFragment$key;
}> = ({ k, a }) => {
  const data = useFragment(fragment, k);

  const total = data.status
    ? BigInt(data.status.accountBond) + BigInt(data.status.liquidBondSize)
    : 0n;

  const account = useFragment(accountFragment, a);
  const acc = account?.strategies.find((a) => a.pool?.id === data.id);

  return (
    <StrategyContainer>
      {/* <Meta
        title={`Rujira ${data.bondAsset.metadata.symbol}/RUNE Automated Market Maker - XYK`}
        description={`Provides liquidity to THORChain ${data.bondAsset.metadata.symbol}/RUNE Continuous Liquidity Pools on the Base Layer, using a constant product (XYX) bonding curve. `}
      /> */}
      {data.status && (
        <Subscription id={data.status.id} subscription={subscription} />
      )}
      <div className="flex ai-c wrap">
        <h1 className="h1 mb-0 mr-2">
          <AssetLabel
            asset={data.bondAsset}
            Container={({ children }) => (
              <small className="color-grey fs-20 fs-lg-28">{children}</small>
            )}
          />
        </h1>
        <div className="lp big mr-3">
          <IconDenom denom={data.bondAsset.metadata.symbol} />
        </div>
        <i18n.div className="tag tag--primary">Staking</i18n.div>
      </div>
      <div className="row pad wrap mt-1">
        <div className="col-12 col-md-8 mt-2">
          <div className="row pad wrap">
            <div className="col-12 col-sm-6">
              <div className="card h-full p-3 flex dir-c">
                <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">
                  Strategy TVL
                </i18n.h3>
                <Fiat
                  symbol="$"
                  amount={BigInt(data.status?.valueUsd || 0)}
                  decimals={8}
                  className="fs-22 fs-md-32 condensed fw-500 mt-1"
                />
                <div className="flex ai-s mt-1">
                  <IconDenom
                    denom={data.bondAsset.metadata.symbol}
                    className="h-3 w-3 mr-1"
                  />
                  <div className="flex dir-c">
                    <Decimal
                      amount={total}
                      round={4}
                      className="fs-18 fs-md-20 lh-18 lh-md-20 condensed color-grey"
                      symbol={data.bondAsset.metadata.symbol}
                    />
                    <div className="fs-14 lh-14 condensed color-grey mt-0.5">
                      (
                      {(
                        (Number(data.status?.liquidBondSize || 0) /
                          Number(total)) *
                        100
                      ).toFixed(2)}
                      % auto-compounding)
                    </div>
                    {/*<hr className="hr mt-1 mb-0.5 opacity-10" />
                     <table className="table table--no-hover table--xs color-grey">
                      <tbody>
                        <tr>
                          <td className="text-right">
                            <Decimal
                              className="mr-0.5"
                              amount={BigInt(data.status?.accountBond || 0)}
                            />
                          </td>
                          <i18n.td>Account Staked</i18n.td>
                        </tr>
                        <tr>
                          <td className="text-right">
                            <Decimal
                              className="mr-0.5"
                              amount={BigInt(data.status?.liquidBondSize || 0)}
                            />
                          </td>
                          <i18n.td>Liquid Staked</i18n.td>
                        </tr>
                      </tbody>
                    </table> */}
                    {/* <div className="fs-14 lh-18 condensed color-grey">
                      <Decimal
                        amount={BigInt(data.status?.liquidBondSize || 0)}
                      />{" "}
                      Liquid Staked
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-sm-6 mt-2 mt-sm-0">
              <div className="card h-full p-3 flex dir-c">
                {data.bondAsset.metadata.symbol === "RUJI" ? (
                  <RujiRevenue />
                ) : (
                  <>
                    <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">
                      APR
                    </i18n.h3>
                    <p className="fs-22 fs-md-32 condensed fw-500 mt-1">
                      {formatApr(data.stakingSummary.apr)}
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="col-12 mt-4">
              <BalanceProvider asset={data.bondAsset}>
                <StakingPoolPageDeposit k={data || undefined} />
              </BalanceProvider>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4 mt-2 gradient-card-container">
          <StakingPoolBalance
            bond={data.bondAsset.metadata.symbol}
            reward={data.revenueAsset.metadata.symbol}
            k={acc || undefined}
          />
        </div>
      </div>
      <hr className="hr mt-8 mb-8 opacity-4" />
      <div className="row wrap pad">
        <div className="col-12 col-md-6">
          <Summary
            bond={data.bondAsset.metadata.symbol}
            reward={data.revenueAsset.metadata.symbol}
            contract={data.address}
          />
        </div>
      </div>
    </StrategyContainer>
  );
};

const query = graphql`
  query StakingPoolPageQuery {
    staking {
      pendingBalances {
        asset {
          asset
          metadata {
            symbol
          }
        }
        amount
        valueUsd
      }
    }
  }
`;

const RujiRevenue: FC = () => {
  const data = useLazyLoadQuery<StakingPoolPageQuery>(query, {});
  const pendingValue = data?.staking.pendingBalances.reduce(
    (a, v) => a + BigInt(v.valueUsd || 0),
    0n
  );

  return (
    <div
      data-tooltip-html={`<div class="w-20">Unconverted balance of the revenue converter, which will be distributed to stakers</div>`}
      data-tooltip-id="global-tip">
      <h3 className="fs-16 lh-22 fw-400 color-grey mb-1 flex ai-c jc-s">
        Pending Revenue
        <Icons.Info className="w-2 h-2 ml-0.5" />
      </h3>
      <div className="flex ai-c jc-s wrap gap-1.5">
        {data
          ? [...data?.staking.pendingBalances]
              .sort((a, b) => Number(b.valueUsd) - Number(a.valueUsd))
              .map((x) => (
                <RevenueToken
                  key={x.asset.asset}
                  denom={x.asset.metadata.symbol}
                  amount={BigInt(x.amount)}
                />
              ))
          : null}
      </div>
      <div className="text-left">
        <Fiat
          amount={pendingValue || 0n}
          decimals={8}
          symbol="$"
          className="mt-2 color-grey"
        />
        <a
          href="https://liquidy.finance/tools/ruji"
          target="_blank"
          className="fs-12 fw-500 color-grey hover-white no-underline flex mt-2">
          Analytics (community dashboard)
          <External className="w-1.5 h-1.5 ml-0.5" />
        </a>
      </div>
    </div>
  );
};

const RevenueToken = ({
  denom,
  amount,
  decimals = 8,
}: {
  denom: string;
  amount: bigint;
  decimals?: number;
}) => {
  if (amount === 0n) return null;
  const value = nFormatter(amount, 0, decimals);
  return (
    <div className="flex ai-c jc-c">
      <IconDenom className="w-2" denom={denom} />
      <h4 className="condensed fs-18 lh-16 fw-500 mb-0 ml-0.5">
        {value === "0"
          ? priceFormatter(amount * 10000n)
          : nFormatter(amount, 0, decimals)}{" "}
        {denom}
      </h4>
    </div>
  );
};

const DETAILS: Record<string, ReactNode> = {
  RUJI: (
    <>
      <i18n.h4 className="h4 mt-5">Revenue</i18n.h4>
      <p className="mb-1">
        Rewards are collected from protocol revenue across all Rujira apps.
      </p>
      <p>
        <a
          href="https://docs.rujira.network/understanding-ruji-token#utility"
          target="_blank"
          className="color-teal">
          More info
        </a>
      </p>
    </>
  ),
};

const Summary: FC<{ bond: string; reward: string; contract: string }> = ({
  bond,
  reward,
  contract,
}) => {
  return (
    <>
      <i18n.h3 className="h3">Strategy Details</i18n.h3>
      <h4 className="h4">{i18n.t("​Strategy")}: Staking</h4>
      <p className="p">
        Stake {bond} in order to earn a share of rewards. Tokens can be staked
        to earn claimable {reward}, or set to auto-compound, buying & staking
        more {bond} with the {reward} rewards automatically.{" "}
        {bond === "TCY" &&
          i18n.t(
            "A 5% service fee is charged on collected rewards when set to auto-compound."
          )}
      </p>
      <i18n.h4 className="h4 mt-5">Withdrawals</i18n.h4>
      <i18n.p className="p">
        Stakers may withdraw their liquidity at any time. There is no lockup
        period.
      </i18n.p>
      {DETAILS[bond] || null}
      <i18n.h4 className="h4 mt-5">Contracts</i18n.h4>

      <p className="p">
        {i18n.t("Staking")}: <AddressLink address={contract} />
      </p>
    </>
  );
};

const deposit = graphql`
  fragment StakingPoolPageDepositFragment on StakingPool {
    address
    bondAsset {
      asset
      type
      chain
      metadata {
        symbol
        decimals
      }
      variants {
        native {
          denom
        }
      }
    }
  }
`;

const StakingPoolPageDeposit: FC<{
  k: StakingPoolPageDepositFragment$key;
}> = ({ k }) => {
  const [liquid, setLiquid] = useState(true);
  const [amount, setAmount] = useState(0n);
  const data = useFragment(deposit, k);
  const { balance, account } = usePreloadedBalance();

  const msg =
    amount && account
      ? new MsgExec(
          { address: account.address, network: "THOR" },
          account.asset,
          amount,
          data.address,
          {
            [liquid ? "liquid" : "account"]: { bond: {} },
          }
        )
      : null;

  return (
    <div className="card h-full p-3">
      <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">Deposit</i18n.h3>
      <DenomInput
        className="mt-2"
        symbol={data.bondAsset.metadata.symbol}
        amount={amount}
        decimals={8}
        onChangeAmount={setAmount}
        max={balance?.balance || 0n}
        maxLabel={`${i18n.t("Balance")}:`}
      />

      <Toggle
        className="toggle--small mt-1"
        label="Auto-compound rewards to increase stake balance"
        checked={liquid}
        onChange={() => setLiquid(!liquid)}
      />

      <p className="fs-14 color-grey mt-2 balance">
        Please note that there is no unbonding time, and you can unstake
        instantly at any time.
      </p>
      <div className="mt-1 block ml-a text-right">
        <MsgProvider msg={msg}>
          <TxButton
            className="button ml-1"
            label={`Stake ${data.bondAsset.metadata.symbol}`}
          />
        </MsgProvider>
      </div>
    </div>
  );
};
